<template>
<m-pageframe
    title="Process History"
>


<v-container
    fluid
    style="padding-left: 30px; padding-right: 30px"
>

    <v-row>
        <v-col cols="6">
            <v-text-field
                autocomplete="off"
                label="Organization ID"
                v-model="organization_id"
                v-on:input="update"
                hint="Enter organization ID to filter processes"
            >
            </v-text-field>
        </v-col>
        <v-col cols="6">
            <v-select
                label="Process Type"
                v-model="process_type"
                :items="process_type_options"
                v-on:change="update"
                clearable
                hint="Filter by process type"
            >
            </v-select>
        </v-col>
    </v-row>

    <v-data-table
        v-if="results != null"
        :headers="headers"
        :items="results"
        :items-per-page="50"
        class="elevation-1"
        :sort-by="['time_create']"
        :sort-desc="[true]"
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
    >
    
        <template v-slot:item.time_create="{ item }">
            {{$rc.pretty(item.time_create, 'date_and_time')}}
        </template>

        <template v-slot:item.time_last_modified="{ item }">
            {{$rc.pretty(item.time_last_modified, 'date_and_time')}}
        </template>

        <template v-slot:item.time_execution_start="{ item }">
            <span v-if="item.time_execution_start">
                {{$rc.pretty(item.time_execution_start, 'date_and_time')}}
            </span>
            <span v-else>-</span>
        </template>

        <template v-slot:item.time_execution_stop="{ item }">
            <span v-if="item.time_execution_stop">
                {{$rc.pretty(item.time_execution_stop, 'date_and_time')}}
            </span>
            <span v-else>-</span>
        </template>

        <template v-slot:item.state="{ item }">
            <v-chip
                :color="getStateColor(item.state)"
                text-color="white"
                small
            >
                {{item.state}}
            </v-chip>
        </template>


        <template v-slot:item.action="{ item }">
            <v-btn
                small
                color="primary"
                v-on:click="viewDetails(item)"
            >
                View Details
            </v-btn>
        </template>
    
    </v-data-table>

    <v-dialog
        v-model="details_dialog"
        max-width="800px"
    >
        <v-card v-if="selected_process">
            <v-card-title>
                Process Details
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="6">
                        <strong>Process ID:</strong> {{selected_process.process_id}}
                    </v-col>
                    <v-col cols="6">
                        <strong>Organization ID:</strong> {{selected_process.organization_id}}
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <strong>Type:</strong> {{selected_process.type}}
                    </v-col>
                    <v-col cols="6">
                        <strong>State:</strong> 
                        <v-chip
                            :color="getStateColor(selected_process.state)"
                            text-color="white"
                            small
                        >
                            {{selected_process.state}}
                        </v-chip>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <strong>Created:</strong> {{$rc.pretty(selected_process.time_create, 'date_and_time')}}
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <strong>Last Modified:</strong> {{$rc.pretty(selected_process.time_last_modified, 'date_and_time')}}
                    </v-col>
                </v-row>
                <v-row v-if="selected_process.time_execution_start">
                    <v-col cols="12">
                        <strong>Execution Start:</strong> {{$rc.pretty(selected_process.time_execution_start, 'date_and_time')}}
                    </v-col>
                </v-row>
                <v-row v-if="selected_process.time_execution_stop">
                    <v-col cols="12">
                        <strong>Execution Stop:</strong> {{$rc.pretty(selected_process.time_execution_stop, 'date_and_time')}}
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <strong>Progress Counter:</strong> {{selected_process.progress_counter}}
                    </v-col>
                </v-row>
                <v-row v-if="selected_process.status">
                    <v-col cols="12">
                        <strong>Status:</strong>
                        <pre>{{JSON.stringify(selected_process.status, null, 2)}}</pre>
                    </v-col>
                </v-row>
                <v-row v-if="selected_process.output">
                    <v-col cols="12">
                        <strong>Output:</strong>
                        <pre>{{JSON.stringify(selected_process.output, null, 2)}}</pre>
                    </v-col>
                </v-row>
                <v-row v-if="selected_process.exception">
                    <v-col cols="12">
                        <strong>Exception:</strong>
                        <pre>{{selected_process.exception}}</pre>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    text
                    v-on:click="details_dialog = false"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <div class="text-caption text-medium-emphasis mb-4">
        *Process history is only available for the last 30 days.
    </div>
</v-container>
</m-pageframe>
</template>

<script>
const _ = require('lodash');
export default {
    data() {
        return {
            organization_id: null,
            process_type: null,
            results: [],
            details_dialog: false,
            selected_process: null,
            process_type_options: [],

            headers: [
                { text: 'Created', value: 'time_create', sortable: true },
                { text: 'Type', value: 'type', sortable: true },
                { text: 'State', value: 'state', sortable: true },
                { text: 'Organization ID', value: 'organization_id', sortable: true },
                { text: 'Last Modified', value: 'time_last_modified', sortable: true },
                { text: 'Execution Start', value: 'time_execution_start', sortable: true },
                { text: 'Execution Stop', value: 'time_execution_stop', sortable: true },
                { text: 'Action', value: 'action', sortable: false },
            ],
        };
    },

    mounted () {
        // Check if organization_id was passed as a query parameter
        if (this.$route.query.organization_id) {
            this.organization_id = this.$route.query.organization_id;
        }
        // Check if process_type was passed as a query parameter
        if (this.$route.query.process_type) {
            this.process_type = this.$route.query.process_type;
        }

        // Load process types from backend
        this.loadProcessTypes();

        this.doUpdate();
    },

    methods: {

        update: _.debounce(function(){
            this.doUpdate();
        }, 751),

        loadProcessTypes()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/process-types',

                'on_success': (data) => {
                        this.process_type_options = data.process_types;
                    },

                'on_error': 'Unable to load process types',
            });
        },

        doUpdate()
        {
            let search_params = {};

            if (this.organization_id) {
                search_params.organization_id = this.organization_id;
            }

            if (this.process_type) {
                search_params.type = this.process_type;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/process',

                'params': search_params,

                'on_success': (data) => {
                        this.results = data.results;
                    },

                'on_error': 'Unable to complete API call',
            });
        },

        getStateColor(state) {
            switch(state) {
                case 'finished': return 'green';
                case 'running': return 'blue';
                case 'pending': return 'orange';
                case 'failed': return 'red';
                default: return 'grey';
            }
        },

        viewDetails(process) {
            this.selected_process = process;
            this.details_dialog = true;
        },
    },
    
}
</script>

<style lang="scss" scoped>
pre {
    background-color: #f5f5f5;
    padding: 10px;
    border-radius: 4px;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
}
</style>
